import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "./imprimir.ts";
// Al tocar un campo numérico se selecciona todo su contenido: lo que se escribe reemplaza el número que ya estaba (si no, el 6 y el 8 quedan "68").
// Con el ratón, el navegador coloca el cursor al soltar el botón y deshace esa selección: por eso se cancela ese primer "soltar".
let numeroRecienEnfocado: EventTarget | null = null;
document.addEventListener("focusin", (e) => {
  const el = e.target;
  if (el instanceof HTMLInputElement && el.type === "number" && !el.readOnly && !el.disabled) {
    numeroRecienEnfocado = el;
    setTimeout(() => { try { el.select(); } catch { /* algunos navegadores no lo permiten */ } }, 0);
  }
});
document.addEventListener("mouseup", (e) => {
  const el = numeroRecienEnfocado; numeroRecienEnfocado = null;
  if (!(el instanceof HTMLInputElement) || e.target !== el) return;
  e.preventDefault();
  const elegir = () => { try { el.select(); } catch { /* algunos navegadores no lo permiten */ } };
  elegir(); setTimeout(elegir, 0); // según el navegador, el cursor se coloca antes o después: se vuelve a seleccionar
}, true);
import { api, cuandoExpire, guardarSesion, leerSesion, incluye, type Sesion } from "./api.ts";
import { Login } from "./pantallas/Login.tsx";
import { Inicio } from "./pantallas/Inicio.tsx";
import { NuevoPaciente } from "./pantallas/NuevoPaciente.tsx";
import { PacienteVista } from "./pantallas/Paciente.tsx";
import { Logo } from "./pantallas/comun.tsx";
import { Agenda } from "./pantallas/Agenda.tsx";
import { MiAgenda, type CuentaClinica } from "./pantallas/MiAgenda.tsx";
import { Mensajes } from "./pantallas/Mensajes.tsx";
import { Respaldos, AvisoRespaldos } from "./pantallas/Respaldos.tsx";
import { CopiasClinica } from "./pantallas/CopiasClinica.tsx";
import { Configuracion, CambiarClave, FormClave } from "./pantallas/Configuracion.tsx";
import { ActivarDosPasos } from "./componentes/DosPasos.tsx";
import { Laboratorio, PortalLaboratorio } from "./pantallas/Laboratorio.tsx";
import { Registro } from "./pantallas/Registro.tsx";
import { Formulario033 } from "./pantallas/Formulario033.tsx";
import { Formulario024 } from "./pantallas/Formulario024.tsx";
import { Bienvenida } from "./pantallas/Bienvenida.tsx";
import { ExpedientePaciente } from "./pantallas/Expediente.tsx";
import { Reportes } from "./pantallas/Reportes.tsx";
import { Comisiones } from "./pantallas/Comisiones.tsx";
import { Firmas } from "./pantallas/Firmas.tsx";
import { Club } from "./pantallas/Club.tsx";
import { Inventario } from "./pantallas/Inventario.tsx";
import { FacturacionPantalla } from "./pantallas/Compras.tsx";
import { Contabilidad } from "./pantallas/Contabilidad.tsx";
import { AvisoLicencia, AvisoFlotante, Plataforma } from "./pantallas/Suscripcion.tsx";
import { useEstadoIA } from "./componentes/EstadoIA.tsx";
import { TableroUniversidad } from "./pantallas/Universidad.tsx";
import { HolaDonna } from "./componentes/HolaDonna.tsx";
import { BotonInstalar } from "./componentes/Instalar.tsx";
import { iniciarPwa, useEnLinea, useVersionNueva } from "./pwa.ts";
import { useClinica, olvidarClinica, recargarClinica, restaurarClinica, acentoInicial } from "./clinica.ts";
import { leerCache, guardarCache } from "./cache.ts";
import "./estilos.css";

/** Rutas con hash: #/inicio · #/nuevo · #/paciente/<id>/<pestaña> (funciona igual dentro de Capacitor). */
function useRuta(): [string[], (r: string) => void] {
  const leer = () => location.hash.replace(/^#\/?/, "").split("?")[0].split("/").filter(Boolean);
  const [ruta, setRuta] = useState(leer);
  useEffect(() => { const f = () => setRuta(leer()); addEventListener("hashchange", f); return () => removeEventListener("hashchange", f); }, []);
  return [ruta, (r: string) => { location.hash = "#/" + r; }];
}

const ENGRANAJE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

function App() {
  const [sesion, setSesion] = useState<Sesion | null>(leerSesion);
  const [demo, setDemo] = useState(false);
  const [consultorio, setConsultorio] = useState(false);
  // instalación nueva (sin clínicas): la primera pantalla es la de bienvenida
  const [instalacion, setInstalacion] = useState<boolean | null>(leerSesion() ? false : null);
  const [conCodigo, setConCodigo] = useState(false);
  useEffect(() => { if (!sesion) api<{ pendiente: boolean; requiereCodigo?: boolean }>("GET", "/api/instalacion").then((r) => { setInstalacion(r.pendiente); setConCodigo(!!r.requiereCodigo); }).catch(() => setInstalacion(false)); }, [sesion === null]);
  const [ruta, ir] = useRuta();
  const enLinea = useEnLinea();
  const [cambiandoClave, setCambiandoClave] = useState(false);
  const estadoIA = useEstadoIA(!!sesion?.usuario.plataforma);
  // al activar o cambiar la verificación en dos pasos se vuelve a leer quién es la persona (ya no sale el aviso ni la pantalla obligatoria)
  const refrescarYo = () => api<Sesion["usuario"]>("GET", "/api/auth/yo").then((u) => { const s = leerSesion(); if (s) { const n = { ...s, usuario: u }; guardarSesion(n); setSesion(n); } }).catch(() => undefined);
  useEffect(() => { addEventListener("donna:yo", refrescarYo); return () => removeEventListener("donna:yo", refrescarYo); }, []);

  useEffect(() => { cuandoExpire(() => setSesion(null)); api<{ demo: boolean; modo?: string }>("GET", "/api/salud").then((s) => { setDemo(s.demo); setConsultorio(s.modo === "consultorio"); }).catch(() => undefined); }, []);
  const clinica = useClinica();
  useEffect(() => { if (sesion) recargarClinica(); }, [sesion?.usuario.clinicaId]);
  useEffect(() => { if (sesion) api<Sesion["usuario"]>("GET", "/api/auth/yo").then((u) => { const s = { ...sesion, usuario: u }; guardarSesion(s); setSesion(s); }).catch(() => undefined); }, [sesion?.token]);

  // un profesional con varias clínicas: selector de clínica y «Mi agenda»
  const [cuentas, setCuentas] = useState<CuentaClinica[]>(() => leerCache<CuentaClinica[]>("cuentas") ?? []);
  const cargarCuentas = () => api<CuentaClinica[]>("GET", "/api/auth/clinicas").then((c) => setCuentas(guardarCache("cuentas", c))).catch(() => setCuentas([]));
  useEffect(() => { if (sesion) cargarCuentas(); else setCuentas([]); }, [sesion?.usuario.id]);
  useEffect(() => { const f = () => { cargarCuentas(); }; addEventListener("donna:clinicas", f); return () => removeEventListener("donna:clinicas", f); }, [sesion?.usuario.id]);
  async function cambiarClinica(usuarioId: string, destino = "inicio") {
    const r = await api<Sesion>("POST", "/api/auth/cambiar-clinica", { usuarioId });
    guardarSesion(r); olvidarClinica();
    location.hash = "#/" + destino; location.reload();
  }

  if (!sesion && instalacion === null) return null;
  if (!sesion && instalacion) return <Bienvenida requiereCodigo={conCodigo} alTerminar={(s) => { guardarSesion(s); setSesion(s); setInstalacion(false); ir("configuracion/empresa"); }} />;
  if (!sesion && ruta[0] === "registro") { const q = new URLSearchParams(location.hash.split("?")[1] ?? ""); return <Registro tipoInicial={q.get("tipo") === "laboratorio" ? "laboratorio" : "clinica"} correoInicial={q.get("correo") ?? ""} alVolver={() => ir("inicio")} />; }
  if (!sesion) return <Login demo={demo} alCrear={consultorio ? undefined : () => ir("registro")} alEntrar={(s) => { guardarSesion(s); restaurarClinica(); setSesion(s); ir("inicio"); }} />;

  const salir = () => { guardarSesion(null); setSesion(null); olvidarClinica(); };
  // contraseña temporal: primero hay que cambiarla
  if (sesion.usuario.debeCambiar) return <CambiarClave obligatoria alCambiar={(token) => { const s = { token, usuario: { ...sesion.usuario, debeCambiar: false } }; guardarSesion(s); setSesion(s); }} alSalir={salir} />;
  // administración y dirección: pasado el plazo, solo pueden activar la verificación en dos pasos
  const dp = sesion.usuario.dosPasos;
  if (dp?.obligatorio && !dp.activo && dp.diasRestantes === 0) return <ActivarDosPasos nombre={sesion.usuario.nombre} alSalir={salir} alTerminar={refrescarYo} />;
  const [pantalla, id, pestana] = ruta;
  // un laboratorio dental solo ve sus órdenes: sin pacientes, agenda ni configuración de la clínica
  if (sesion.usuario.rol === "laboratorio") return (
    <>
      <header className="top">
        <span className="brand"><Logo />Donna</span>
        <div className="yo">
          <span>{sesion.usuario.nombre}</span>
          <button className="btn chico" onClick={() => setCambiandoClave(!cambiandoClave)} aria-pressed={cambiandoClave}>Contraseña</button>
          <button className="btn chico" onClick={salir}>Salir</button>
        </div>
      </header>
      <main className="wrap">
        {!enLinea && <div className="aviso error sin-red" role="alert">Sin conexión a internet. Lo que escribas ahora no se guardará hasta que vuelva la conexión.</div>}
        {cambiandoClave && <section className="card"><h2>Cambiar mi contraseña</h2><FormClave alCambiar={(token) => { const s = { token, usuario: sesion.usuario }; guardarSesion(s); setSesion(s); setCambiandoClave(false); }} /></section>}
        <PortalLaboratorio />
      </main>
    </>
  );
  return (
    <>
      <header className="top">
        <button className="brand" onClick={() => ir("inicio")} aria-label="Ir al inicio"><Logo />Donna</button>
        <div className="yo">
          {clinica?.logoUrl && <img className="logo-clinica" src={clinica.logoUrl} alt={`Logo de ${clinica.nombre}`} />}
          <span title={clinica?.nombre ?? sesion.usuario.clinica}>{sesion.usuario.nombre}{(clinica?.nombre ?? sesion.usuario.clinica) ? ` · ${clinica?.nombre ?? sesion.usuario.clinica}` : ""}</span>
          {cuentas.length > 1 && (
            <select className="entrada selector-clinica" aria-label="Cambiar de clínica" value={cuentas.find((x) => x.actual)?.usuarioId ?? ""} onChange={(e) => { if (e.target.value) cambiarClinica(e.target.value).catch(() => undefined); }}>
              {cuentas.map((x) => <option key={x.usuarioId} value={x.usuarioId}>{x.clinica}</option>)}
            </select>
          )}
          {cuentas.length > 1 && ["odontologo", "especialista"].includes(sesion.usuario.rol) && <button className="btn chico" onClick={() => ir("mi-agenda")} aria-pressed={pantalla === "mi-agenda"}>Mi agenda</button>}
          {sesion.usuario.plataforma && <button className="btn chico" onClick={() => ir("plataforma")} aria-pressed={pantalla === "plataforma"}>Panel Donna{estadoIA?.nivel === "problema" && <span title={estadoIA.titulo} aria-label={`Atención: ${estadoIA.titulo}`} style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "var(--red)", marginLeft: 6 }} />}</button>}
          {sesion.usuario.academico && sesion.usuario.permisos.some((x) => x.startsWith("academico:")) && <button className="btn chico" onClick={() => ir("universidad")} aria-pressed={pantalla === "universidad"}>Clínica universitaria</button>}
          <BotonInstalar />
          {incluye("hola_donna") && <HolaDonna ruta={ruta} ir={ir} />}
          <button className="gear" onClick={() => ir("configuracion")} aria-label="Configuración" title="Configuración" aria-pressed={pantalla === "configuracion"}>{ENGRANAJE}</button>
          <button className="btn chico" onClick={salir}>Salir</button>
        </div>
      </header>
      <main className="wrap">
        {!enLinea && <div className="aviso error sin-red" role="alert">Sin conexión a internet. Lo que escribas ahora no se guardará hasta que vuelva la conexión.</div>}
        {sesion.usuario.permisos.includes("respaldos:admin") && <AvisoRespaldos ir={ir} />}
        <AvisoLicencia ir={ir} />
        <AvisoFlotante />
        {pantalla === "nuevo" ? <NuevoPaciente alCrear={(p) => ir(`paciente/${p.id}/ficha`)} alCancelar={() => ir("inicio")} />
          : pantalla === "agenda" ? <Agenda ir={ir} pacienteInicial={new URLSearchParams(location.hash.split("?")[1] ?? "").get("paciente") ?? undefined} />
          : pantalla === "mi-agenda" && cuentas.length > 1 ? <MiAgenda cuentas={cuentas} ir={ir} cambiar={(u, destino) => { cambiarClinica(u, destino).catch(() => undefined); }} />
          : pantalla === "mensajes" ? <Mensajes ir={ir} pestana={id ?? ""} />
          : pantalla === "respaldos" ? <Respaldos />
          : pantalla === "mis-copias" ? <CopiasClinica />
          : pantalla === "configuracion" ? <Configuracion ir={ir} pestana={id ?? ""} />
          : pantalla === "reportes" && sesion.usuario.permisos.includes("reportes:leer") ? <Reportes />
          : pantalla === "contabilidad" && sesion.usuario.permisos.includes("reportes:leer") ? <Contabilidad ir={ir} />
          : pantalla === "comisiones" && sesion.usuario.permisos.some((x) => x.startsWith("comisiones:")) ? <Comisiones />
          : pantalla === "inventario" && sesion.usuario.permisos.includes("inventario:leer") ? <Inventario />
          : pantalla === "laboratorio" && sesion.usuario.permisos.includes("laboratorio:leer") ? <Laboratorio />
          : pantalla === "club" && sesion.usuario.permisos.includes("club:afiliar") ? <Club />
          : pantalla === "firmas" && sesion.usuario.permisos.some((x) => x === "clinico:delegar" || x === "clinico:firmar") ? <Firmas />
          : pantalla === "plataforma" && sesion.usuario.plataforma ? <Plataforma />
          : pantalla === "universidad" && sesion.usuario.academico ? <TableroUniversidad ir={ir} />
          : pantalla === "facturas" && sesion.usuario.permisos.includes("caja:leer") ? <FacturacionPantalla ir={ir} />
          : pantalla === "expediente" && id ? <ExpedientePaciente key={id} id={id} volver={() => ir(`paciente/${id}`)} />
          : pantalla === "formulario-033" && id && sesion.usuario.permisos.includes("clinico:leer") ? <Formulario033 key={id} id={id} volver={() => ir(`paciente/${id}`)} />
          : pantalla === "formulario-024" && id && pestana && sesion.usuario.permisos.includes("clinico:leer") ? <Formulario024 key={pestana} pacienteId={id} consentimientoId={pestana} volver={() => ir(`paciente/${id}/documentos`)} />
          : pantalla === "paciente" && id ? <PacienteVista key={id} id={id} pestana={pestana ?? ""} irA={(t) => ir(`paciente/${id}/${t}`)} />
          : <Inicio ir={ir} />}
      </main>
    </>
  );
}

/** Aviso de versión nueva: se ve en cualquier pantalla (también antes de entrar) y no recarga solo, para no perder lo que se está escribiendo. */
function AvisoVersionNueva() {
  const nueva = useVersionNueva();
  const [oculto, setOculto] = useState(false);
  useEffect(() => { if (!oculto) return; const t = setTimeout(() => setOculto(false), 10 * 60 * 1000); return () => clearTimeout(t); }, [oculto]);
  if (!nueva || oculto) return null;
  return (
    <div className="aviso-version" role="status">
      <span><b>Hay una versión nueva de Donna.</b> Actualiza para tener las últimas mejoras y correcciones.</span>
      <button className="btn chico pri" onClick={() => location.reload()}>Actualizar ahora</button>
      <button className="btn chico" onClick={() => setOculto(true)}>Más tarde</button>
    </div>
  );
}

acentoInicial();
iniciarPwa();
createRoot(document.getElementById("app")!).render(<StrictMode><App /><AvisoVersionNueva /></StrictMode>);
